import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FileText, Flame, CircleDashed, ClipboardCheck, Recycle,
  ShieldX, XCircle, Layers, Droplets, GitBranch, PenLine,
  Monitor, RefreshCw, FlaskConical, CalendarCheck, FileCheck,
  AlertTriangle, CalendarClock, Box, Wrench, Settings, Zap,
  PackageSearch, Siren, ShoppingCart, FileInput, Briefcase,
  PackageCheck, Truck, TrendingDown, Wallet, ShoppingBag,
  Bot, BarChart3, Trash2, Factory, ClipboardList, Warehouse,
  CircleX, FolderCog, ChevronLeft, LayoutGrid, Image, Users,
  Activity, LogOut
} from 'lucide-react';
import { ROUTES } from '../constants/routes';
import { useAuth } from '../context/AuthContext';
import { isAdmin } from '../utils/roleUtils';
import './ERPDashboard.css';

// ─── Module Hierarchy Config ───────────────────────────────────────────────
const MODULE_CONFIG = [
  {
    id: 'raw_material_planning',
    label: 'Raw Material Planning',
    color: '#2E7D46',
    lightColor: '#e8f5ed',
    Icon: FolderCog,
    iconFile: 'Raw Material Planning.png',
    order: 1,
    children: [
      { id: 'raw_material_log',        label: 'Raw Material Log',        Icon: FileText,       iconFile: 'Raw Material Log.png',         route: ROUTES.RAW_MATERIAL },
      { id: 'furnace_dashboard',        label: 'Furnace Dashboard',       Icon: BarChart3,      iconFile: 'Furnace Planner Log.png',      route: ROUTES.FURNACE_DASHBOARD, adminOnly: true },
      { id: 'continuous_caster',        label: 'Continuous Caster',       Icon: Factory,        iconFile: 'Continuous Caster.png',        route: ROUTES.CASTER },
      { id: 'furnace_raw_material',     label: 'Furnace Raw Material',    Icon: Flame,          iconFile: 'Furnace Raw material.png',     route: ROUTES.FURNACE_MATERIAL, adminOnly: true },
      { id: 'daily_furnace_usage',      label: 'Daily Furnace Usage',     Icon: Activity,       iconFile: 'Furnace Planner Log.png',      route: ROUTES.DAILY_FURNACE_USAGE },
      { id: 'coil_stock',              label: 'Coil Stock',              Icon: CircleDashed,   iconFile: 'Coil Stock.png',               route: ROUTES.FURNACE_STOCK },
      { id: 'furnace_planner_log',     label: 'Furnace Planner Log',     Icon: ClipboardCheck, iconFile: 'Furnace Planner Log.png',      route: ROUTES.FURNACE },
      { id: 'scrap_entry',             label: 'Scrap Entry',             Icon: Recycle,        iconFile: 'Confirm Scrap.png',            route: ROUTES.SCRAP },
      { id: 'melting_scrap',           label: 'Melting Scrap',           Icon: Droplets,       iconFile: 'Melting Scrap.png',            route: ROUTES.MELTING_SCRAP },
      { id: 'final_rejection',         label: 'Final Rejection',         Icon: XCircle,        iconFile: 'Final Rejection.png',          route: ROUTES.INTERNAL_REJECTION },
    ],
  },
  {
    id: 'production',
    label: 'Production',
    color: '#1F3864',
    lightColor: '#e8ecf4',
    Icon: Factory,
    iconFile: 'Production.png',
    order: 2,
    children: [
      { id: 'production_dashboard', label: 'Production Dashboard', Icon: Activity, iconFile: 'Production.png', route: ROUTES.PRODUCTION_DASHBOARD },
      { id: 'extrusion_log',     label: 'Extrusion Log',      Icon: GitBranch,    iconFile: 'Extrusion Log.png',      route: ROUTES.EXTRUSION },
      { id: 'draw_log',          label: 'Draw Log',            Icon: PenLine,      iconFile: 'Draw Log.png',           route: ROUTES.DRAW },
      { id: 'vmc_log',           label: 'VMC Log',             Icon: Monitor,      iconFile: 'VMC Log.png',            route: ROUTES.VMC },
      { id: 'rolling_log',       label: 'Rolling Log',         Icon: RefreshCw,    iconFile: 'Rolling Log.png',        route: ROUTES.ROLLING },
      { id: 'testing_log',       label: 'Testing Log',         Icon: FlaskConical, iconFile: 'Testing Log.png',        route: ROUTES.QUALITY },
      { id: 'day_working_report',label: 'Day Working Report',  Icon: CalendarCheck,iconFile: 'Day Working Report.png', route: ROUTES.DAY_WORKING },
      { id: 'test_certificate',  label: 'Test Certificate',    Icon: FileCheck,    iconFile: 'Test Certificate.png',   route: ROUTES.QUALITY },
    ],
  },
  {
    id: 'breakdown',
    label: 'Breakdown',
    color: '#6A4C93',
    lightColor: '#f0ebf8',
    Icon: Wrench,
    iconFile: 'Breakdown.png',
    order: 3,
    children: [
      { id: 'breakdown_process',            label: 'Breakdown Application',      Icon: AlertTriangle,  iconFile: 'Breakdown Request.png',         route: ROUTES.BREAKDOWN_PROCESS },
      { id: 'die_planner',                  label: 'Die Planner',                 Icon: CalendarClock,  iconFile: 'Die Planner.png',               route: ROUTES.DIE_PLANNER },
      { id: 'die_stock',                    label: 'Die Store & Management',      Icon: Box,            iconFile: 'Die Stock.png',                 route: ROUTES.DIE_STORE },
      { id: 'tools_stock',                  label: 'Tools Stock',                 Icon: Wrench,         iconFile: 'Tools Stock.png',               route: ROUTES.MAINTENANCE },
      { id: 'maintenance_log',              label: 'Maintenance Log',             Icon: Settings,       iconFile: 'Maintenance Log.png',           route: ROUTES.MAINTENANCE },
      { id: 'electricity_consumption_log',  label: 'Electricity Consumption Log', Icon: Zap,            iconFile: 'Electricity Consumption Log.png',route: ROUTES.ELECTRICITY_CONSUMPTION },
      { id: 'china_stock',                  label: 'China Stock',                 Icon: PackageSearch,  iconFile: 'China Stock.png',               route: ROUTES.MAINTENANCE },
      { id: 'emergency_stock',              label: 'Emergency Stock',             Icon: Siren,          iconFile: 'Emergency Stock.png',           route: ROUTES.MAINTENANCE },
    ],
  },
  {
    id: 'order',
    label: 'Order',
    color: '#8B4513',
    lightColor: '#f5ede6',
    Icon: ClipboardList,
    iconFile: 'Order.png',
    order: 4,
    children: [
      { id: 'sales_order',       label: 'Sales Order',       Icon: ShoppingCart, iconFile: 'Sales Order.png',      route: ROUTES.ORDERS },
      { id: 'purchase_order',    label: 'Purchase Order',    Icon: FileInput,    iconFile: 'Purchase Order.png',   route: ROUTES.PURCHASE_ORDERS },
      { id: 'job_work',          label: 'Job Work',          Icon: Briefcase,    iconFile: 'Job Work.png',         route: ROUTES.JOBWORK },
      { id: 'packing',           label: 'Packing',           Icon: PackageCheck, iconFile: 'Packing.png',          route: ROUTES.PACKING_NEW },
      { id: 'dispatch',          label: 'Dispatch',          Icon: Truck,        iconFile: 'Dispatch.png',         route: ROUTES.DISPATCH_NEW },
      { id: 'order_raw_material_log', label: 'Raw Material Log',  Icon: FileText,     iconFile: 'Raw Material Log.png', route: ROUTES.RAW_MATERIAL },
      { id: 'order_debit_note',  label: 'Debit Note',        Icon: TrendingDown, iconFile: 'Debit Note.png',       route: ROUTES.DEBIT_NOTE },
      { id: 'order_vendor_details', label: 'Vendor Details',  Icon: Users,        iconFile: 'Breakdown Request.png', route: ROUTES.BREAKDOWN_PROCESS },
    ],
  },
  {
    id: 'store',
    label: 'Store',
    color: '#1F6F6F',
    lightColor: '#e6f3f3',
    Icon: Warehouse,
    iconFile: 'store.png',
    order: 5,
    children: [
      { id: 'consumption_log',      label: 'Consumption Log',      Icon: TrendingDown, iconFile: 'Consumption Log.png',      route: ROUTES.CONSUMPTION },
      { id: 'consumption_cost_log', label: 'Consumption Cost Log', Icon: Wallet,       iconFile: 'Consumption Cost Log.png', route: ROUTES.CONSUMPTION },
      { id: 'procurement_log',      label: 'Procurement Log',      Icon: ShoppingBag,  iconFile: 'Procurement Log.jfif',     route: ROUTES.PROCUREMENT },
    ],
  },
  {
    id: 'x',
    label: 'X',
    color: '#4A4A4A',
    lightColor: '#efefef',
    Icon: CircleX,
    iconFile: 'X.png',
    order: 6,
    children: [
      { id: 'ai',                label: 'AI',                Icon: Bot,      iconFile: 'AI.png',                route: ROUTES.AI_ASSISTANT },
      { id: 'production_tracker',label: 'Production Tracker', Icon: BarChart3,iconFile: 'Production Tracker.png', route: ROUTES.PRODUCTION_TRACKER },
      { id: 'pending_deletion',  label: 'Pending Deletion',  Icon: Trash2,   iconFile: 'Pending Deletion.png',  route: ROUTES.DELETION_APPROVALS },
      { id: 'smart_weight_pilot', label: 'Smart Weight (Pilot)', Icon: Zap,   iconFile: null,                    route: ROUTES.SMART_WEIGHT_PILOT, adminOnly: true },
      { id: 'app_gallery',       label: 'App Gallery',       Icon: Image,    iconFile: 'App Gallery.png',       route: ROUTES.OFFLINE_PHOTOS },
      { id: 'customer_feedback', label: 'Customer Feedback', Icon: ClipboardCheck, iconFile: 'Customer Feedback.png', route: ROUTES.CUSTOMER_FEEDBACK },
    ],
  },
];

// ─── Icon component with image fallback ───────────────────────────────────
function ModuleIcon({ iconFile, Icon, size = 48, accent }) {
  const [imgError, setImgError] = useState(false);
  if (iconFile && !imgError) {
    return (
      <img
        src={`/module-icons/${encodeURIComponent(iconFile)}`}
        alt=""
        onError={() => setImgError(true)}
        style={{ width: size, height: size, objectFit: 'contain', borderRadius: 8 }}
      />
    );
  }
  return <Icon size={size} color={accent || '#555'} strokeWidth={1.5} />;
}

// ─── Main Dashboard Component ─────────────────────────────────────────────
export default function ERPDashboard({ operator }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { logout } = useAuth();

  const moduleParam = searchParams.get('module');

  const [activeParent, setActiveParent] = useState(() => {
    if (moduleParam) {
      return MODULE_CONFIG.find((m) => m.id === moduleParam) || null;
    }
    return null;
  });
  const [overviewExpanded, setOverviewExpanded] = useState(null);

  useEffect(() => {
    if (moduleParam) {
      const found = MODULE_CONFIG.find((m) => m.id === moduleParam);
      if (found) {
        setActiveParent(found);
      }
    } else {
      setActiveParent(null);
    }
  }, [moduleParam]);

  const handleParentClick = useCallback((mod) => {
    setActiveParent(mod);
    setSearchParams({ module: mod.id });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [setSearchParams]);

  const handleChildClick = useCallback((child) => {
    if (child.route) navigate(child.route);
  }, [navigate]);

  const handleBack = useCallback(() => {
    setActiveParent(null);
    setSearchParams({});
  }, [setSearchParams]);

  return (
    <div className="erp-dashboard">
      {/* ── Header ── */}
      <header className="erp-header">
        <div className="erp-header-inner">
          <div className="erp-logo">
            <span className="erp-logo-icon">⚙️</span>
            <div>
              <div className="erp-logo-title">Jay Copper &amp; Alloys</div>
              <div className="erp-logo-sub">ERP Navigation</div>
            </div>
          </div>
          {operator && (
            <div className="erp-user" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div className="erp-user-avatar">
                  {(operator.name || 'U')[0].toUpperCase()}
                </div>
                <div className="erp-user-name">{operator.name || 'Operator'}</div>
              </div>
              <button 
                onClick={logout} 
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#dc2626',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  borderRadius: '4px'
                }}
                title="Logout"
              >
                <LogOut size={18} />
                <span className="mobile-hide" style={{ fontSize: '14px', fontWeight: 500 }}>Logout</span>
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="erp-main">
        {/* ── Breadcrumb ── */}
        <nav className="erp-breadcrumb">
          <span
            className={`erp-crumb ${!activeParent ? 'erp-crumb--active' : 'erp-crumb--link'}`}
            onClick={activeParent ? handleBack : undefined}
          >
            Dashboard
          </span>
          {activeParent && (
            <>
              <span className="erp-crumb-sep">›</span>
              <span className="erp-crumb erp-crumb--active" style={{ color: activeParent.color }}>
                {activeParent.label}
              </span>
            </>
          )}
        </nav>

        {/* ── LEVEL 0: Parent Cards ── */}
        {!activeParent && (
          <section className="erp-section">
            <div className="erp-section-header">
              <LayoutGrid size={20} />
              <h1 className="erp-section-title">Main Panel</h1>
              <span className="erp-section-sub">Select a module to explore</span>
            </div>
            <div className="erp-parent-grid">
              {MODULE_CONFIG.map((mod) => (
                <button
                  key={mod.id}
                  className="erp-parent-card"
                  style={{ '--accent': mod.color, '--light': mod.lightColor }}
                  onClick={() => handleParentClick(mod)}
                  aria-label={`Open ${mod.label}`}
                >
                  <span className="erp-parent-num">{String(mod.order).padStart(2, '0')}</span>
                  <div className="erp-parent-icon-wrap">
                    <ModuleIcon iconFile={mod.iconFile} Icon={mod.Icon} size={56} accent={mod.color} />
                  </div>
                  <div className="erp-parent-label">{mod.label}</div>
                  <div className="erp-parent-count">
                    {mod.children.filter(child => !child.adminOnly || isAdmin(operator)).length} modules
                  </div>
                  <div className="erp-parent-arrow">→</div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* ── LEVEL 1: Children Panel ── */}
        {activeParent && (
          <section className="erp-children-section">
            <div
              className="erp-children-header"
              style={{ '--accent': activeParent.color, '--light': activeParent.lightColor }}
            >
              <button className="erp-back-btn" onClick={handleBack} aria-label="Back to Main">
                <ChevronLeft size={18} /> Back to Main
              </button>
              <div className="erp-children-header-title">
                <ModuleIcon iconFile={activeParent.iconFile} Icon={activeParent.Icon} size={32} accent="#fff" />
                <span>{activeParent.label}</span>
              </div>
              <span className="erp-children-count">
                {activeParent.children.filter(child => !child.adminOnly || isAdmin(operator)).length} modules
              </span>
            </div>

            {activeParent.children.length === 0 ? (
              <div className="erp-empty-state">
                <span className="erp-empty-icon">📭</span>
                <p>No modules configured yet</p>
              </div>
            ) : (
              <div className="erp-children-grid">
                {activeParent.children
                  .filter(child => !child.adminOnly || isAdmin(operator))
                  .map((child, idx) => (
                  <button
                    key={child.id}
                    className="erp-child-card"
                    style={{ '--accent': activeParent.color, '--light': activeParent.lightColor }}
                    onClick={() => handleChildClick(child)}
                    aria-label={`Open ${child.label}`}
                  >
                    <span className="erp-child-num">{String(idx + 1).padStart(2, '0')}</span>
                    <div className="erp-child-icon-wrap">
                      <ModuleIcon iconFile={child.iconFile} Icon={child.Icon} size={44} accent={activeParent.color} />
                    </div>
                    <div className="erp-child-label">{child.label}</div>
                  </button>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ── All Parent Modules Overview ── */}
        {!activeParent && (
          <section className="erp-overview-section">
            <div className="erp-section-header">
              <LayoutGrid size={18} />
              <h2 className="erp-section-title erp-section-title--sm">All Parent Modules</h2>
              <span className="erp-section-sub">Click to view children</span>
            </div>
            <div className="erp-overview-grid">
              {MODULE_CONFIG.map((mod) => (
                <div
                  key={mod.id}
                  className={`erp-overview-card ${overviewExpanded === mod.id ? 'erp-overview-card--open' : ''}`}
                  style={{ '--accent': mod.color, '--light': mod.lightColor }}
                >
                  <button
                    className="erp-overview-header"
                    onClick={() => setOverviewExpanded(overviewExpanded === mod.id ? null : mod.id)}
                    aria-expanded={overviewExpanded === mod.id}
                  >
                    <div className="erp-overview-icon">
                      <ModuleIcon iconFile={mod.iconFile} Icon={mod.Icon} size={28} accent={mod.color} />
                    </div>
                    <span className="erp-overview-label">{mod.label}</span>
                    <span className="erp-overview-chevron">{overviewExpanded === mod.id ? '▲' : '▼'}</span>
                  </button>
                  <div className="erp-overview-children">
                    {mod.children.filter(child => !child.adminOnly || isAdmin(operator)).map((child, idx) => (
                      <button
                        key={child.id}
                        className="erp-overview-child"
                        onClick={() => handleChildClick(child)}
                        aria-label={child.label}
                      >
                        <span className="erp-overview-child-num">{idx + 1}.</span>
                        <span className="erp-overview-child-label">{child.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
